import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Icon, { IconVariant } from "@galaxy-io/dls/icons/Icon";
import Box from "@galaxy-io/dls/layout/Box";
import Stepper, { StepperSize } from "@galaxy-io/dls/navigation/Stepper";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import type {
  ImportFile,
  ImportKind,
  ImportResolution,
  ImportSettings,
} from "@gtm/contracts/imports";
import {
  CaretLeftIcon,
  DownloadSimpleIcon,
  FileCsvIcon,
  UploadSimpleIcon,
} from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { MenuItem, OverflowMenu } from "@/components/workspace/overflow-menu";
import { RelativeTime } from "@/components/workspace/relative-time";
import { Feedback } from "@/components/workspace/status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { apiClient, unwrap } from "@/lib/api-client";
import { count, downloadTemplate, fileUrl } from "./import-fields";
import { FileBar, ImportBody, ImportFooter, Stacked } from "./import-layout";
import { MappingStep } from "./import-mapping";
import { PAGE_SIZE, ReviewStep, rowState } from "./import-review";
import { NewSourceDialog } from "./import-source";
import { importStatus, UploadStep } from "./import-upload";

const MAX_BYTES = 10 * 1024 * 1024;
type Step = "upload" | "map" | "review";
const stepLabels: Record<Step, string> = {
  upload: "Step 1 of 3 · Upload a CSV file",
  map: "Step 2 of 3 · Choose a source and match columns",
  review: "Step 3 of 3 · Review and import",
};
// Everything an import can change, refreshed once a run settles.
const affected = [
  "people",
  "companies",
  "activity-page",
  "signal-board",
  "signal-swipe",
  "company-options",
];
const emptySettings: ImportSettings = { mapping: {}, sourceId: null, sources: {} };

export function ImportButton({ kind }: { kind: ImportKind }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        label="Import CSV"
        variant={ButtonVariant.SECONDARY}
        icon={UploadSimpleIcon}
        onClick={() => setOpen(true)}
      />
      <ImportDialog open={open} kind={kind} close={() => setOpen(false)} />
    </>
  );
}

function download(url: string) {
  const a = document.createElement("a");
  a.href = url;
  a.click();
}

function ImportDialog({
  open = true,
  kind,
  close,
}: {
  kind: ImportKind;
  close: () => void;
  open?: boolean;
}) {
  const cache = useQueryClient();
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<ImportFile | null>(null);
  const [settings, setSettings] = useState<ImportSettings>(emptySettings);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [creatingSource, setCreatingSource] = useState(false);

  const sources = useQuery({
    queryKey: ["import-sources"],
    enabled: open,
    queryFn: () => unwrap(apiClient().GET("/imports/sources")),
  });
  const history = useQuery({
    queryKey: ["imports"],
    enabled: open,
    queryFn: () => unwrap(apiClient().GET("/imports")),
  });
  const execution = useQuery({
    queryKey: ["import-run", file?.id],
    enabled: open && !!file,
    queryFn: () =>
      unwrap(apiClient().GET("/imports/{id}/run", { params: { path: { id: file!.id } } })),
    refetchInterval: 2000,
  });
  const running = execution.data?.status === "RUNNING";
  const locked = busy || running || (!!file && (execution.isPending || !!execution.error));
  const detail = useQuery({
    queryKey: ["import", file?.id, page, execution.data?.status],
    refetchInterval: running ? 2000 : false,
    enabled: open && !!file && step !== "upload",
    queryFn: () =>
      unwrap(
        apiClient().GET("/imports/{id}", {
          params: { path: { id: file!.id }, query: { page: String(page) } },
        }),
      ),
  });
  useEffect(() => {
    if (!file?.id || !execution.data?.runId || !execution.data?.status || running) return;
    void cache.invalidateQueries({
      predicate: (q) => ["import", "imports", ...affected].includes(String(q.queryKey[0])),
    });
  }, [cache, file?.id, execution.data?.runId, execution.data?.status, running]);
  useDialogReset(open, () => {
    setStep("upload");
    setFile(null);
    setSettings(emptySettings);
    setPage(1);
    setBusy(false);
    setUploading(null);
    setError("");
    setCreatingSource(false);
  });

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }
  function openImport(next: ImportFile, nextStep?: Step) {
    setFile(next);
    setSettings(next.settings);
    setPage(1);
    setError("");
    setStep(
      nextStep ??
        (next.settings.sourceId || Object.keys(next.settings.sources).length ? "review" : "map"),
    );
  }
  function startOver() {
    setFile(null);
    setSettings(emptySettings);
    setStep("upload");
    setError("");
  }
  async function upload(selected: File) {
    setError("");
    if (!/\.csv$/i.test(selected.name) && selected.type !== "text/csv")
      return setError("Choose a .csv file. Export spreadsheets as CSV first.");
    if (selected.size > MAX_BYTES)
      return setError("That file is over 10 MB. Split it and try again.");
    setUploading(selected.name);
    await run(async () => {
      const saved = await unwrap(
        apiClient().POST("/imports", {
          body: { kind, filename: selected.name, csv: await selected.text() },
        }),
      );
      // With a single source there is nothing to choose.
      const only = sources.data?.length === 1 ? sources.data[0]!.id : null;
      openImport({ ...saved, settings: { ...saved.settings, sourceId: only } }, "map");
      await history.refetch();
    });
    setUploading(null);
  }
  async function saveSettings(next: ImportSettings) {
    const saved = await unwrap(
      apiClient().PATCH("/imports/{id}", { params: { path: { id: file!.id } }, body: next }),
    );
    setFile(saved);
    await cache.invalidateQueries({ queryKey: ["import", file!.id] });
  }
  async function resolve(number: number, resolution: ImportResolution) {
    await unwrap(
      apiClient().PATCH("/imports/{id}/rows/{number}", {
        params: { path: { id: file!.id, number: String(number) } },
        body: resolution,
      }),
    );
    await detail.refetch();
  }
  async function importReady() {
    await unwrap(apiClient().POST("/imports/{id}/commit", { params: { path: { id: file!.id } } }));
    await execution.refetch();
    await Promise.all([detail.refetch(), history.refetch()]);
    await cache.invalidateQueries({ predicate: (q) => affected.includes(String(q.queryKey[0])) });
  }

  const sourceOptions = (sources.data || []).map((s) => ({
    value: s.id,
    label: `${s.name} · ${s.provider}`,
  }));

  const completed = detail.data?.completed ?? file?.completed ?? 0;
  const done = !!file && completed >= file.total;
  const pages = file ? Math.ceil(file.total / PAGE_SIZE) : 1;
  const ready = detail.data?.rows.filter((r) => rowState(r) === "ready").length ?? 0;
  const skipping = detail.data?.rows.filter((r) => rowState(r) === "skip").length ?? 0;
  const hasSource = !!settings.sourceId || !!settings.mapping.source;

  return (
    <>
      <NewSourceDialog
        open={open && creatingSource}
        onClose={() => setCreatingSource(false)}
        onSaved={async (source) => {
          await sources.refetch();
          setSettings((s) => ({ ...s, sourceId: source.id }));
          setCreatingSource(false);
        }}
      />

      <SurfaceDialog
        open={open}
        flush
        width={720}
        title={`Import ${kind}`}
        headerIcon={UploadSimpleIcon}
        headerDescription={stepLabels[step]}
        headerActions={
          step === "upload" && (
            <Button
              size={ButtonSize.SMALL}
              label="Download template"
              icon={DownloadSimpleIcon}
              variant={ButtonVariant.TERTIARY}
              onClick={() => downloadTemplate(kind)}
            />
          )
        }
        busy={busy}
        onClose={() => {
          if (!busy) close();
        }}
        footer={
          <ImportFooter>
            <div>
              {step === "map" && (
                <Button
                  label="Back"
                  icon={CaretLeftIcon}
                  variant={ButtonVariant.TERTIARY}
                  isDisabled={busy}
                  onClick={startOver}
                />
              )}
              {step === "review" && !completed && (
                <Button
                  label="Edit columns"
                  icon={CaretLeftIcon}
                  variant={ButtonVariant.TERTIARY}
                  isDisabled={locked}
                  onClick={() => setStep("map")}
                />
              )}
              {step === "review" && file && (
                <OverflowMenu
                  size={ButtonSize.MEDIUM}
                  label="Downloads"
                  icon={DownloadSimpleIcon}
                  variant={ButtonVariant.TERTIARY}
                >
                  {(closeMenu) => (
                    <>
                      <MenuItem
                        icon={FileCsvIcon}
                        label="Original file"
                        onClick={() => {
                          closeMenu();
                          download(fileUrl(file.id, "original"));
                        }}
                      />
                      <MenuItem
                        icon={DownloadSimpleIcon}
                        label="Rows not yet imported"
                        onClick={() => {
                          closeMenu();
                          download(fileUrl(file.id, "rejected"));
                        }}
                      />
                    </>
                  )}
                </OverflowMenu>
              )}
            </div>
            <div>
              <Button
                label="Close"
                variant={ButtonVariant.SECONDARY}
                isDisabled={busy}
                onClick={close}
              />
              {step === "map" && (
                <Button
                  label="Continue"
                  isLoading={busy}
                  isDisabled={locked || !hasSource}
                  onClick={() =>
                    run(async () => {
                      await saveSettings(settings);
                      setPage(1);
                      setStep("review");
                    })
                  }
                />
              )}
              {step === "review" &&
                (done ? (
                  <Button label="Done" onClick={close} />
                ) : (
                  <Button
                    label={
                      running
                        ? "Importing…"
                        : pages > 1
                          ? "Import ready rows"
                          : !ready && !skipping
                            ? "Import rows"
                            : skipping && !ready
                              ? `Skip ${count(skipping, "row")}`
                              : `Import ${count(ready + skipping, "row")}`
                    }
                    isLoading={running || busy}
                    isDisabled={locked || !detail.data || (pages === 1 && !ready && !skipping)}
                    onClick={() => run(importReady)}
                  />
                ))}
            </div>
          </ImportFooter>
        }
      >
        <Box padding={[12, 16]}>
          <Stepper
            ariaLabel="Import progress"
            size={StepperSize.SMALL}
            steps={[{ label: "Upload" }, { label: "Map columns" }, { label: "Review" }]}
            value={completed ? 3 : step === "upload" ? 0 : step === "map" ? 1 : 2}
          />
        </Box>
        <ImportBody $flush={step === "upload"}>
          {step !== "upload" && <Feedback message={error} error />}
          {sources.error && step !== "upload" && (
            <Feedback message="Could not load sources. Close and reopen to try again." error />
          )}
          {file && execution.error && (
            <Feedback message="Could not check import status. Retrying…" error />
          )}
          {file && step !== "upload" && (
            <FileBar>
              <Icon component={FileCsvIcon} size={20} variant={IconVariant.SECONDARY} />
              <Stacked>
                <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM} lineClamp={1}>
                  {file.filename}
                </Text>
                <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY} lineClamp={1}>
                  {count(file.total, "row")} · {file.headers.length}{" "}
                  {file.headers.length === 1 ? "column" : "columns"} · Uploaded{" "}
                  <RelativeTime value={file.createdAt} label="Uploaded" />
                </Text>
              </Stacked>
              {step === "review" ? importStatus({ ...file, completed }) : null}
            </FileBar>
          )}
          {step === "upload" && (
            <UploadStep
              kind={kind}
              uploading={uploading}
              history={history.data}
              historyError={history.error || undefined}
              notice={<Feedback message={error} error />}
              onRetry={() => void history.refetch()}
              onFile={(f) => void upload(f)}
              onOpen={(f) => openImport(f)}
            />
          )}
          {step === "map" && file && (
            <MappingStep
              file={file}
              settings={settings}
              sample={page === 1 ? detail.data?.rows[0]?.data : undefined}
              sourceOptions={sourceOptions}
              locked={locked}
              onChange={setSettings}
              onCreateSource={() => setCreatingSource(true)}
            />
          )}
          {step === "review" && file && (
            <ReviewStep
              file={file}
              settings={settings}
              detail={detail.data}
              execution={execution.data}
              page={page}
              locked={locked}
              sourceOptions={sourceOptions}
              onPage={setPage}
              onResolve={(n, r) => run(() => resolve(n, r))}
              onSource={(value, id) => {
                const next = { ...settings, sources: { ...settings.sources } };
                if (id) next.sources[value] = id;
                else delete next.sources[value];
                setSettings(next);
                void run(() => saveSettings(next));
              }}
            />
          )}
        </ImportBody>
      </SurfaceDialog>
    </>
  );
}
